import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Calendar, Target, Award } from 'lucide-react';
import { getUserAnalytics } from '../services/analyticsService.js';

const PerformanceTrends = ({ userId, compact = false }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      const data = await getUserAnalytics(userId);
      setAnalytics(data);
      setLoading(false);
    };

    if (userId) {
      fetchAnalytics();
    }
  }, [userId]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          <span className="ml-3 text-gray-600">Loading analytics...</span>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="text-center py-12">
          <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">No performance data available yet</p>
          <p className="text-sm text-gray-500 mt-2">Complete some tests to see your trends!</p>
        </div>
      </div>
    );
  }

  // Get trend icon
  const getTrendIcon = () => {
    switch (analytics.performanceTrend) {
      case 'improving':
        return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'declining':
        return <TrendingDown className="w-5 h-5 text-red-600" />;
      default:
        return <Minus className="w-5 h-5 text-gray-600" />;
    }
  };

  // Get trend color
  const getTrendColor = () => {
    switch (analytics.performanceTrend) {
      case 'improving':
        return 'text-green-600 bg-green-50';
      case 'declining':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  // Get trend message
  const getTrendMessage = () => {
    switch (analytics.performanceTrend) {
      case 'improving':
        return 'Your performance is improving! Keep up the great work! 🚀';
      case 'declining':
        return 'Your scores have been declining. Consider reviewing the material.';
      default:
        return 'Your performance is consistent. Push yourself to improve!';
    }
  };

  // Filter trends by selected period
  const filteredTrends = analytics.recentPerformance.slice(0,
    selectedPeriod === 'week' ? 1 :
    selectedPeriod === 'month' ? 4 : analytics.recentPerformance.length
  );

  // Calculate max score for chart scaling
  const maxScore = 100;

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${compact ? 'p-4' : 'p-6'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <TrendingUp className="w-6 h-6 text-red-600" />
          <h2 className={`${compact ? 'text-lg' : 'text-xl'} font-bold text-gray-900`}>
            Performance Trends
          </h2>
        </div>

        {/* Period Selector */}
        <div className="flex items-center space-x-2 bg-gray-100 rounded-lg p-1">
          {[
            { value: 'week', label: 'Week' },
            { value: 'month', label: 'Month' },
            { value: 'all', label: 'All Time' }
          ].map(period => (
            <button
              key={period.value}
              onClick={() => setSelectedPeriod(period.value)}
              className={`
                px-3 py-1.5 rounded-md text-sm font-medium transition-all
                ${selectedPeriod === period.value
                  ? 'bg-white text-red-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }
              `}
            >
              {period.label}
            </button>
          ))}
        </div>
      </div>

      {/* Overall Trend Card */}
      <div className={`p-4 rounded-lg ${getTrendColor()} mb-6`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {getTrendIcon()}
            <div>
              <p className="font-semibold">Overall Trend: {analytics.performanceTrend.charAt(0).toUpperCase() + analytics.performanceTrend.slice(1)}</p>
              <p className="text-sm mt-1">{getTrendMessage()}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold">{analytics.averageScore}%</p>
            <p className="text-sm">Avg Score</p>
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-600 font-medium">Total Tests</p>
          <p className="text-2xl font-bold text-blue-900 mt-1">{analytics.totalTests}</p>
        </div>

        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <p className="text-sm text-green-600 font-medium">Best Score</p>
          <p className="text-2xl font-bold text-green-900 mt-1">{analytics.highestScore}%</p>
        </div>

        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
          <p className="text-sm text-purple-600 font-medium">This Week</p>
          <p className="text-2xl font-bold text-purple-900 mt-1">{analytics.testsThisWeek}</p>
        </div>

        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
          <p className="text-sm text-orange-600 font-medium">This Month</p>
          <p className="text-2xl font-bold text-orange-900 mt-1">{analytics.testsThisMonth}</p>
        </div>
      </div>

      {/* Chart Area */}
      {filteredTrends.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            Weekly Progress
          </h3>

          <div className="relative h-64 bg-gray-50 rounded-lg p-4">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between py-4 pr-2 text-xs text-gray-500">
              <span>100%</span>
              <span>75%</span>
              <span>50%</span>
              <span>25%</span>
              <span>0%</span>
            </div>

            {/* Grid lines */}
            <div className="absolute left-12 right-0 top-0 bottom-0 flex flex-col justify-between py-4">
              {[0, 1, 2, 3, 4].map(i => (
                <div key={i} className="border-t border-gray-200"></div>
              ))}
            </div>

            {/* Bars */}
            <div className="absolute left-12 right-0 bottom-4 top-4 flex items-end justify-around">
              {filteredTrends.map((trend, index) => {
                const height = (trend.averageScore / maxScore) * 100;
                const barColor = trend.category === 'improving' ? 'bg-green-500' :
                                trend.category === 'declining' ? 'bg-red-500' : 'bg-blue-500';

                return (
                  <div key={index} className="flex flex-col items-center flex-1 mx-1">
                    {/* Bar */}
                    <div className="w-full flex items-end justify-center" style={{ height: '100%' }}>
                      <div
                        className={`${barColor} rounded-t-lg transition-all duration-500 hover:opacity-80 cursor-pointer relative group w-full max-w-[60px]`}
                        style={{ height: `${height}%` }}
                      >
                        {/* Tooltip on hover */}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap pointer-events-none">
                          {trend.averageScore}% avg
                          <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                        </div>

                        {/* Score label */}
                        <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-gray-700">
                          {trend.averageScore}%
                        </span>
                      </div>
                    </div>

                    {/* Week label */}
                    <p className="text-xs text-gray-600 mt-2 text-center">
                      {new Date(trend.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                    <p className="text-xs text-gray-500">{trend.testCount} test{trend.testCount !== 1 ? 's' : ''}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Strong & Weak Skills */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Strong Skills */}
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="font-semibold text-green-900 mb-3 flex items-center">
            <Award className="w-4 h-4 mr-2" />
            Strong Skills
          </h4>
          <div className="space-y-2">
            {analytics.strongSkills.length > 0 ? (
              analytics.strongSkills.map((skill, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                  <span className="text-sm text-green-900">{skill}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-green-700">Complete more tests to identify strengths!</p>
            )}
          </div>
        </div>

        {/* Weak Skills */}
        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
          <h4 className="font-semibold text-orange-900 mb-3 flex items-center">
            <Target className="w-4 h-4 mr-2" />
            Areas to Improve
          </h4>
          <div className="space-y-2">
            {analytics.weakSkills.length > 0 ? (
              analytics.weakSkills.map((skill, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-orange-600 rounded-full"></div>
                  <span className="text-sm text-orange-900">{skill}</span>
                </div>
              ))
            ) : (
              <p className="text-sm text-orange-700">Doing great across all areas!</p>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      {!compact && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Analytics updated: {analytics.lastUpdated.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>
        </div>
      )}
    </div>
  );
};

export default PerformanceTrends;

